import { Component, computed, OnDestroy, OnInit, Signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ContentComponent } from '../../components/content/content.component';
import { ReactiveFormsModule } from '@angular/forms';
import { loadUserDetails, updateUserDetails } from '../../store/actions/user-details.actions';
import { selectLoading, selectPending, selectUser } from '../../store/selectors/user-details.selectors';
import { UserDetails } from '../../store/reducers/user-details.reducer';
import { UserDetailsForm } from './user-details.form';
import { BreadcrumbsComponent } from '../../common/breadcrumbs/breadcrumbs.component';
import { ValidationErrorsComponent } from '../../common/validation-errors/validation-errors.component';
import { NgxMaskDirective } from 'ngx-mask';
import { SpinnerComponent } from '../../common/spinner/spinner.component';
import { AlertComponent } from '../../common/alert/alert.component';
import {
  AlertContainerComponent,
  AlertService
} from '../../common/alert-container/alert-container.component';
import { SubpageAppEntitySubpageRead } from '../../openapi';
import { selectSubpages } from '../../store/selectors/app.selectors';
import { SubpageContentByUrlPipe } from '../../pipes/subpage-content-by-url.pipe';
import { AccordionGroupComponent } from '../../common/accordion-group/accordion-group.component';
import { AccordionItemComponent } from '../../common/accordion-item/accordion-item.component';
import { Subscription } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { ScrollInToViewDirective } from '../../directives/scroll-in-to-view.directive';

@Component({
  selector: 'app-my-details-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ContentComponent,
    BreadcrumbsComponent,
    ReactiveFormsModule,
    ValidationErrorsComponent,
    NgxMaskDirective,
    SpinnerComponent,
    AlertComponent,
    AlertContainerComponent,
    SubpageContentByUrlPipe,
    AccordionGroupComponent,
    AccordionItemComponent,
    ScrollInToViewDirective
  ],
  providers: [
    UserDetailsForm
  ],
  templateUrl: './my-details-page.component.html'
})
export class MyDetailsPageComponent implements OnInit, OnDestroy {

  public user: Signal<UserDetails | null> = this.store$.selectSignal(selectUser);

  public loading: Signal<boolean> = this.store$.selectSignal(selectLoading);

  public pending: Signal<boolean> = this.store$.selectSignal(selectPending);

  public subpages: Signal<SubpageAppEntitySubpageRead[]> = this.store$.selectSignal(selectSubpages);

  public isCompletedUserData: Signal<boolean> = computed(() => !!this.user()?.gdprConsent && !!this.user()?.email && !!this.user()?.termsConsent);

  private fetchUser: Subscription = toObservable(this.user).subscribe(user => {
    this.patchForm();
    if (this.isCompletedUserData()) {
      this.form.enable();
    }
  });

  constructor(
    private store$: Store,
    public form: UserDetailsForm,
    private notificationService: AlertService,
    private route: ActivatedRoute,
    private router: Router
  ) {
  }

  public patchForm(): void {
    this.form.patchFormValues(this.user());
    this.form.disable();
  }

  public onSubmit(): void {
    if (this.form.valid) {
      this.store$.dispatch(
        updateUserDetails({
          user: this.form.toWriteValue(),
          successFnc: () => {
            this.router.navigate(['/strona-glowna'])
              .then(() => {
                this.router.navigate(['/moje-dane'])
                  .then(() => {
                    this.notificationService.show({
                      title: 'Status danych:',
                      content: 'Twoje dane zostały zaktualizowane.',
                      type: 'success'
                    });
                  });
              });
          },
          failureFnc: () => {
            this.notificationService.show({
              title: 'Błąd zapisu danych:',
              content: 'Skontaktuj się z administratorem systemu.',
              type: 'danger'
            });
          }
        })
      );
    } else {
      this.form.markAllAsTouched();
    }
  }

  public ngOnInit(): void {
    this.store$.dispatch(loadUserDetails());
  }

  public ngOnDestroy(): void {
    this.fetchUser.unsubscribe();
  }
}
